import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', slate: '#5C6B7A', bg: '#F5F7FA', border: 'rgba(136,150,168,0.15)' };

/**
 * ModuleShell — wraps each module's content with a header + tab strip
 * Used by all 11 modules for consistent layout.
 */
export default function ModuleShell({ title, subtitle, tabs, action, children }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || tabs[0]?.id || '';

  const setTab = (tabId) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', tabId);
      return next;
    });
  };

  const currentTab = tabs.find(t => t.id === activeTab) || tabs[0];

  return (
    <div className="space-y-0" data-testid={`module-${title?.toLowerCase().replace(/\s/g, '-')}`}>
      {/* Module Header */}
      <div className="flex items-center justify-between mb-1">
        <div>
          <h1 className="text-xl sm:text-2xl font-medium tracking-tight" style={{ color: C.navy, fontFamily: "'IBM Plex Sans', sans-serif" }}>
            {title}
          </h1>
          {subtitle && <p className="text-xs mt-0.5" style={{ color: C.slate, fontFamily: 'Georgia, serif', letterSpacing: '0.02em' }}>{subtitle}</p>}
        </div>
        {action && <div>{action}</div>}
      </div>

      {/* Tab Strip */}
      {tabs.length > 1 && (
        <div className="flex gap-0 border-b mb-4 overflow-x-auto" style={{ borderColor: C.border }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`px-4 py-2.5 text-[13px] font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-[#2A6FC4] text-[#0D1B2A]'
                  : 'border-transparent text-[#5C6B7A] hover:text-[#0D1B2A] hover:border-[rgba(136,150,168,0.3)]'
              }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className="ml-1.5 text-[10px] font-normal px-1.5 py-0.5 rounded-full"
                  style={{ background: activeTab === tab.id ? 'rgba(42,111,196,0.1)' : 'rgba(136,150,168,0.1)', color: activeTab === tab.id ? '#2A6FC4' : '#5C6B7A' }}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Tab Content */}
      <div className="stagger-in" key={activeTab}>
        {currentTab?.content || children}
      </div>
    </div>
  );
}
